// src/api.js
import axios from "axios";

// Backend base URL
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || "",
  withCredentials: true, // send cookies for session auth
});

// No JWT or special headers required now – cookie carries the session
api.interceptors.request.use((config) => config);

// Let callers handle 401 / errors
api.interceptors.response.use(
  (res) => res,
  (err) => Promise.reject(err)
);

// -------------------------------------------------------------------
// Auth
// -------------------------------------------------------------------

export async function login(email, password) {
  const r = await api.post("/login", { email, password });
  // Backend returns: { access_token, token_type, must_change_password, ... }
  return r.data;
}

export async function changePassword(currentPassword, newPassword) {
  const r = await api.post("/change-password", {
    current_password: currentPassword,
    new_password: newPassword,
  });
  return r.data;
}

export async function logout() {
  const r = await api.post("/logout");
  return r.data;
}

// -------------------------------------------------------------------
// Organizations
// -------------------------------------------------------------------

export async function getOrgs() {
  const r = await api.get("/organizations");
  return r.data || [];
}

// Optional helper used by OrgList.js
export async function addOrg(name, connectionUrl) {
  const payload = { name, connection_url: connectionUrl };
  const { data } = await api.post("/organizations", payload);
  return data;
}

// -------------------------------------------------------------------
// Flags (org-first view)
// -------------------------------------------------------------------

export async function getFlags(orgId, search = "") {
  if (orgId === undefined || orgId === null) {
    return [];
  }

  // Backend requires org_ids (CSV)
  const params = { org_ids: String(orgId) };

  // Optional search wiring (adjust to match backend if needed)
  if (search && search.trim()) {
    params.feature_names = search.trim();
  }

  const r = await api.get("/features", { params });
  const data = r.data || {};

  // New backend shape: { features: [...] }
  if (Array.isArray(data.features)) {
    return data.features;
  }

  // Backward-compat fallback if feature_states exists
  const states = data.feature_states || {};
  const byId =
    states[orgId] ||
    states[String(orgId)] ||
    [];

  return Array.isArray(byId) ? byId : [];
}

// Bulk update flags for org(s) via /features/update
export async function updateFlags(orgIds, featureNames, enabled) {
  const payload = {
    org_ids: orgIds,
    feature_names: featureNames,
    enabled,
  };
  const r = await api.post("/features/update", payload);
  return r.data;
}


// -------------------------------------------------------------------
// Feature-first helpers (matrix + global feature list)
// -------------------------------------------------------------------

// Return a sorted array of feature names as strings
export async function listAllFeatures() {
  const r = await api.get("/features");
  const data = r.data || {};
  const features = data.features || [];

  if (!Array.isArray(features)) return [];

  return features
    .map((f) =>
      typeof f === "string" ? f : f.feature_name || f.name || null
    )
    .filter(Boolean)
    .sort();
}

// Low-level matrix fetch (orgIds & featureNames are arrays)
export async function getMatrix(orgIds, featureNames) {
  const params = {};

  if (orgIds && orgIds.length) {
    params.org_ids = orgIds.join(",");
  }
  if (featureNames && featureNames.length) {
    params.feature_names = featureNames.join(",");
  }

  const r = await api.get("/matrix", { params });
  return r.data;
}

// High-level helper used by FeatureFirstTab: fetch matrix for a single feature
export async function listFeatureMatrix(featureName) {
  const params = {};
  if (featureName) {
    params.feature_names = featureName;
  }
  const r = await api.get("/matrix", { params });
  const data = r.data || {};
  // Backend returns FeatureOrgStatusList: { items: [...] }
  return data && Array.isArray(data.items) ? data : { items: [] };
}

// Matrix-wide bulk update
// Be flexible:
//  - bulkUpdateMatrix([1,2], ["FLAG_A","FLAG_B"], true)
//  - bulkUpdateMatrix([1,2], [{ feature_name: "FLAG_A", enabled: true }, ...])
export async function bulkUpdateMatrix(orgIds, updatesOrFeatures, enabled) {
  let updates = [];

  if (Array.isArray(updatesOrFeatures) && updatesOrFeatures.length) {
    const first = updatesOrFeatures[0];

    if (typeof first === "object" && first.feature_name !== undefined) {
      // Already in [{ feature_name, enabled }, ...] form
      updates = updatesOrFeatures;
    } else {
      // Assume it's a list of feature names + single enabled flag
      updates = updatesOrFeatures.map((name) => ({
        feature_name: name,
        enabled: !!enabled,
      }));
    }
  }

  const payload = {
    org_ids: orgIds,
    updates,
  };

  const r = await api.post("/matrix/update", payload);
  return r.data;
}

// Used by FeatureFirstTab:
// bulkUpdateFeature(feature, [orgId], enabled)
export async function bulkUpdateFeature(featureName, orgIds, enabled) {
  return bulkUpdateMatrix(orgIds, [
    { feature_name: featureName, enabled: !!enabled },
  ]);
}

// Default export so `import api from "./api"` works
export default api;
